/**
 * Guard against React ↔ browser-translation DOM crashes.
 *
 * Google Translate (and similar text-rewriting layers) wrap or replace text
 * nodes, detaching them from the parent React still tracks. When React's commit
 * phase later calls removeChild / insertBefore against the original parent, the
 * browser throws `NotFoundError: ... not a child of this node` — which unwinds
 * React and blanks the page for that visitor. Observed on the paid
 * /implementations/ landing page for a zh-HK visitor auto-translating the en-us
 * page (PostHog issue 019f91d3); the stack was pure react-dom reconciliation.
 *
 * Fix — the widely-used React workaround (facebook/react#11538): make these two
 * DOM ops no-op when the node isn't actually a child, instead of throwing. React
 * reconciles correctly on its next pass and the page stays up. Idempotent; safe
 * to call before hydration.
 */
export function installTranslateCrashGuard(): void {
  if (typeof Node === 'undefined' || !Node.prototype) return;

  const proto = Node.prototype as unknown as {
    __translateGuardInstalled?: boolean;
    removeChild: (child: Node) => Node;
    insertBefore: (newNode: Node, referenceNode: Node | null) => Node;
  };
  if (proto.__translateGuardInstalled) return;
  proto.__translateGuardInstalled = true;

  const originalRemoveChild = proto.removeChild;
  proto.removeChild = function (this: Node, child: Node): Node {
    // Already detached by an external layer (e.g. Google Translate) — no-op.
    if (child.parentNode !== this) return child;
    return originalRemoveChild.call(this, child);
  };

  const originalInsertBefore = proto.insertBefore;
  proto.insertBefore = function (this: Node, newNode: Node, referenceNode: Node | null): Node {
    if (referenceNode && referenceNode.parentNode !== this) return newNode;
    return originalInsertBefore.call(this, newNode, referenceNode);
  };
}
